var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import KB from '@kb/global-context';
import { useTranslation } from '@kb/i18n';
import { IconSymbol } from '@ocp/button';
import { notifyDanger, notifySuccess } from '@ocp/notifications';
import { getButtonChecker } from '../actions-handlers/check';
import { closeAllCollapses, openAllCollapses } from '../actions-handlers/collapses';
import { getButtonItems, getShareButtons } from '../actions-handlers/share';
import i18nKeys from '../i18n/index';
import { useAddToComparisonMutation, useAddToFavoriteContentMutation, useDeleteFromFavoriteContentMutation, useSubscribeContentMutation, useUnsubscribeContentMutation, } from '../mutations/index';
const useButtons = (queryParams, buttonsData, buttonsToAdd, buttonsToRemove, containerRef, onMutate, onComparisonConflict) => {
    var _a, _b, _c;
    let buttons = [];
    const { t } = useTranslation();
    /*
     There is ugly hack, refetch query simulation. In case when initData is available
     not possible to call useToolbarContentQuery hook
     then refetch API and apollo cache not working for that hook
    */
    const [addToFavorite] = useAddToFavoriteContentMutation(queryParams.classNameId, queryParams.classPk);
    const [deleteFromFavorite] = useDeleteFromFavoriteContentMutation(queryParams.classNameId, queryParams.classPk);
    const [subscribe] = useSubscribeContentMutation(queryParams.classNameId, queryParams.classPk, onMutate);
    const [unsubscribe] = useUnsubscribeContentMutation(queryParams.classNameId, queryParams.classPk, onMutate);
    const [addToComparison] = useAddToComparisonMutation(queryParams.classNameId, queryParams.classPk, t(i18nKeys.ADDED), (_b = (_a = (buttonsData.addToComparisonListButton || buttonsToAdd.addToComparisonListButton)) === null || _a === void 0 ? void 0 : _a.comparisonPageUrl) !== null && _b !== void 0 ? _b : '', onComparisonConflict);
    const checkButton = getButtonChecker(buttonsData, buttonsToRemove, buttonsToAdd);
    checkButton('favoriteButton', (buttonInfo) => {
        buttons.push({
            order: buttonInfo.order,
            symbol: buttonInfo.pressed ? IconSymbol.KnowStarColor : IconSymbol.KnowStar,
            title: buttonInfo.pressed ? t(i18nKeys.REMOVE_FROM_FAVORITES) : t(i18nKeys.ADD_TO_FAVORITES),
            onClick: () => __awaiter(void 0, void 0, void 0, function* () {
                if (buttonInfo.pressed) {
                    yield deleteFromFavorite();
                }
                else {
                    yield addToFavorite();
                }
            }),
        });
    });
    checkButton('subscriptionButton', (buttonInfo) => {
        buttons.push({
            order: buttonInfo.order,
            symbol: buttonInfo.pressed ? IconSymbol.BellOnFull : IconSymbol.BellOn,
            title: buttonInfo.pressed ? t(i18nKeys.UNSUBSCRIBE) : t(i18nKeys.SUBSCRIBE),
            onClick: () => {
                if (buttonInfo.pressed) {
                    unsubscribe();
                }
                else {
                    subscribe();
                }
            },
        });
    });
    checkButton('collapseToggler', (buttonInfo) => {
        buttons.push({
            order: buttonInfo.order,
            symbol: IconSymbol.OpenAllCollapse,
            title: t(i18nKeys.EXPAND_ALL_COLLAPSES),
            onClick: () => openAllCollapses(containerRef.current),
        }, {
            order: buttonInfo.order + 1,
            symbol: IconSymbol.CloseAllCollapse,
            title: t(i18nKeys.SHRINK_ALL_COLLAPSES),
            onClick: () => closeAllCollapses(containerRef.current),
        });
    });
    checkButton('printButton', (buttonInfo) => {
        buttons.push({
            order: buttonInfo.order,
            symbol: IconSymbol.Print,
            title: t(i18nKeys.PRINT),
            onClick: () => window.open(buttonInfo.previewUrl),
        });
    });
    checkButton('conversionsButton', (buttonInfo) => {
        const button = {
            order: buttonInfo.order,
            symbol: IconSymbol.Download,
            title: t(i18nKeys.DOWNLOAD),
        };
        if (buttonInfo.conversions.length > 1) {
            button.items = buttonInfo.conversions.map((item) => ({
                title: item.format === 'ORIGINAL' ? t(i18nKeys.ORIGINAL_FILE) : item.format,
                onClick: () => window.open(item.url),
            }));
        }
        else {
            button.onClick = () => window.open(buttonInfo.conversions[0].url);
        }
        buttons.push(button);
    });
    checkButton('shareButton', (buttonInfo) => {
        const sharesCount = Object
            .entries(buttonInfo)
            .filter(([key, value]) => ['sendToSmsButton', 'sendToEmailButton', 'contentLinkButton'].includes(key) && value)
            .length;
        if (sharesCount <= buttonInfo.countButtonsToMerge) {
            buttons = buttons.concat(getShareButtons(buttonInfo));
        }
        else {
            buttons.push({
                order: buttonInfo.order,
                symbol: IconSymbol.Share,
                title: t(i18nKeys.SHARE),
                items: getButtonItems(buttonInfo),
            });
        }
    });
    checkButton('checkOutButton', (buttonInfo) => {
        buttons.push({
            order: buttonInfo.order,
            symbol: buttonInfo.pressed ? IconSymbol.Lock : IconSymbol.Unlock,
            title: buttonInfo.pressed ? t(i18nKeys.CANCEL_CHECKOUT) : t(i18nKeys.CHECKOUT),
            onClick: () => __awaiter(void 0, void 0, void 0, function* () {
                try {
                    yield fetch(buttonInfo.url, { method: 'POST' });
                    notifySuccess(t(i18nKeys.YOUR_REQUEST_COMPLETED_SUCCESSFULLY));
                    onMutate();
                }
                catch (e) {
                    notifyDanger(t(i18nKeys.ERROR_IS_OCCURED));
                }
            }),
        });
    });
    checkButton('openLinkButton', (buttonInfo) => {
        buttons.push({
            order: buttonInfo.order,
            symbol: IconSymbol.Shortcut,
            title: t(i18nKeys.OPEN_IN_NEW_TAB),
            onClick: () => window.open(buttonInfo.url, '_blank'),
        });
    });
    checkButton('addToComparisonListButton', (buttonInfo) => {
        buttons.push({
            order: buttonInfo.order,
            symbol: IconSymbol.SelectFromList,
            title: t(i18nKeys.TO_COMPARISON_LIST),
            onClick: () => {
                if (buttonInfo.pressed) {
                    KB.navigatePath(buttonInfo.comparisonPageUrl);
                }
                else {
                    addToComparison();
                }
            },
        });
    });
    const editButton = (_c = (!buttonsToRemove.editButton && buttonsData.editButton)) !== null && _c !== void 0 ? _c : buttonsToAdd.editButton;
    if (editButton) {
        const url = editButton.url.replace('redirectPlaceholder', encodeURIComponent(window.location.toString()));
        buttons.push({
            order: editButton.order,
            symbol: IconSymbol.Pencil,
            title: t(i18nKeys.EDIT),
            onClick: () => KB.navigatePath(url),
        });
    }
    return buttons.sort((a, b) => a.order - b.order);
};
export default useButtons;
